"use client";

import { Container, Box } from "@mui/material";
import Image from "next/image";
import Link from "next/link";
import { useState, useEffect } from "react";
import LanguageSelectorCADA from "./LanguageSelectorCADA";
import { getStoredLocale, normalizeLocaleForContext } from "@/utils/localeCookie";

interface EnrollmentHeaderProps {
    tenant: "nada" | "cada" | "atd";
}

export default function EnrollmentHeader({ tenant }: EnrollmentHeaderProps) {
    const [currentLocale, setCurrentLocale] = useState<"en" | "fr">("en");

    useEffect(() => {
        // Check current locale from cookie
        const storedLocale = getStoredLocale();
        if (storedLocale) {
            setCurrentLocale(normalizeLocaleForContext(storedLocale));
        }

        // Listen for locale changes
        const handleLocaleChange = (e: CustomEvent<{ locale: string }>) => {
            setCurrentLocale(normalizeLocaleForContext(e.detail.locale));
        };

        window.addEventListener("localeChange", handleLocaleChange as EventListener);
        return () =>
            window.removeEventListener("localeChange", handleLocaleChange as EventListener);
    }, []);

    const getHeaderImage = () => {
        switch (tenant) {
            case "cada":
                return currentLocale === "fr"
                    ? "/cada_2026_header_fr.png"
                    : "/cada_2026_header_en.png";
            case "atd":
                return "/atd_2026_header.jpg";
            default:
                return "/nada_2026_header.jpg";
        }
    };

    const getTenantName = () => {
        switch (tenant) {
            case "cada":
                return "CADA";
            case "atd":
                return "ATD";
            default:
                return "NADA";
        }
    };

    return (
        <Container maxWidth="lg" sx={{ pt: 3 }}>
            {/* Header Image with Language Selector for CADA */}
            <Box sx={{ position: "relative", mb: 2 }}>
                <Link href="/portal" style={{ display: "block", cursor: "pointer" }}>
                    <Image
                        src={getHeaderImage()}
                        alt={`${getTenantName()} Dealership Workforce Study`}
                        width={1920}
                        height={280}
                        style={{ width: "100%", height: "auto", display: "block" }}
                        priority
                    />
                </Link>
                {/* Language Selector - Top Right for CADA */}
                {tenant === "cada" && (
                    <Box
                        sx={{
                            position: "absolute",
                            top: 16,
                            right: 16,
                            zIndex: 10,
                        }}
                    >
                        <LanguageSelectorCADA />
                    </Box>
                )}
            </Box>
        </Container>
    );
}
